import type { EssayCopilotAnswer } from '@app/@types/redux/answer';
import type { EssayCopilotOptions } from '@app/@types/redux/pageElement';
import assistantAvatar from '@app/assets/images/assistant-avatar.png';
import smile from '@app/assets/images/icons/smile.svg';
import { useStreamingChat, type ChatMessage } from '@libs/hooks/useStreamingChat';
import logger from '@libs/log';
import { nanoid } from '@reduxjs/toolkit';
import { useAppSelector } from '@state/redux/store';
import Api from '@state/utils/Api';
import { translate } from '@state/utils/helper';
import classNames from 'classnames';
import moment from 'moment';
import type Quill from 'quill';
import 'quill/dist/quill.snow.css';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { IoIosSend } from 'react-icons/io';
import {
  PiArrowRight,
  PiBookOpenText,
  PiBrainBold,
  PiChats,
  PiChecks,
  PiClipboardText,
  PiFileText,
  PiFolderSimplePlusLight,
  PiKeyboard,
  PiLightbulb,
  PiMagicWand,
} from 'react-icons/pi';
import Markdown from 'react-markdown';
import SlidingPane from 'react-sliding-pane';
import ReactTextareaAutosize from 'react-textarea-autosize';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

import {
  Alert,
  Button,
  Col,
  H1,
  ImagePlaceholder,
  Indicator,
  Modal,
  Row,
  UncontrolledTooltip,
} from '@components/UI/Html';
// env import removed as it's now handled by the hook
import { errorToast, successToast } from '@libs/helper';
import { getPageElementDisabledState } from '@state/selectors/projects';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { Tabs } from 'radix-ui';
import { FiCopy } from 'react-icons/fi';
import { HiOutlineArrowLeft } from 'react-icons/hi';
import { MdDone } from 'react-icons/md';
import './EssayCopilot.scss';

// Constants moved outside component to prevent recreation
const DEFAULT_STATES = { copilotStarted: null, copilotEnded: null } as const;
const SYSTEM_PROMPT = (
  activityResource: string
) => `You are Cartedo's TedoGPT, a helpful assistant designed to support learners in their educational journey. Your role is to provide guidance, answer questions, help with essay writing, and offer constructive feedback to help learners improve their skills. Be encouraging, clear, and educational in your responses. Always aim to help the learner understand concepts better and develop their critical thinking abilities.

You are given a resource to help the learner with their essay. The resource is:

${activityResource}

Always use the resource to help the learner with their essay.`;

enum TABS {
  CHAT = 'chat',
  PROMPT_LIBRARY = 'prompt-library',
  GUIDELINES = 'guidelines',
  ASSESSMENT_GUIDELINES = 'assessment-guidelines',
  RESOURCE = 'resource',
}

// Loading component moved outside to prevent recreation
const Loading = (
  <svg height="40" width="45" className="typing-loader">
    <circle className="typing-dot" cx="10" cy="20" r="4" />
    <circle className="typing-dot" cx="25" cy="20" r="4" />
    <circle className="typing-dot" cx="40" cy="20" r="4" />
  </svg>
);

// Use ChatMessage from the hook instead of ChatItem
type ChatItem = ChatMessage;

interface EssayCopilotViewProps {
  currentAnswer: EssayCopilotAnswer | undefined;
  options: EssayCopilotOptions;
  preview?: boolean;
  answer?: (answer: EssayCopilotAnswer) => void;
  pageElementId: string;
}

const EssayCopilotView = memo<EssayCopilotViewProps>(
  ({ currentAnswer, preview, answer, pageElementId, options }) => {
    const states = useMemo(() => currentAnswer?.states ?? DEFAULT_STATES, [currentAnswer?.states]);

    const messages = useMemo(
      () => currentAnswer?.essayCopilotMessages ?? [],
      [currentAnswer?.essayCopilotMessages]
    );

    const [focus, setFocus] = useState(false);
    const editorRef = useRef<Quill | null>(null);

    const value = useMemo(() => currentAnswer?.essay ?? '', [currentAnswer?.essay]);

    const handleTextChangeBlur = useCallback(() => {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      if (preview || !editorRef.current) return;
      const content = editorRef.current.root.innerHTML;
      answer?.({
        ...currentAnswer,
        essay: content,
      } as EssayCopilotAnswer);
    }, [preview, answer, currentAnswer]);

    const [isOpen, setIsOpen] = useState(false);
    const [isInstructionsOpen, setIsInstructionsOpen] = useState(false);

    const [currentTab, setCurrentTab] = useState(TABS.CHAT);

    const disabled = useAppSelector((state) =>
      getPageElementDisabledState(state.projects, pageElementId)
    );

    // Memoize event handlers
    const handleOpenPane = useCallback(() => {
      setIsOpen(true);
    }, []);

    const handleClosePane = useCallback(() => {
      setIsOpen(false);
    }, []);

    const handleFocusIn = useCallback(() => {
      setFocus(true);
    }, []);

    const handleFocusOut = useCallback(() => {
      setFocus(false);
    }, []);

    // Memoize expensive class computations
    const containerClasses = useMemo(
      () =>
        classNames('essay-copilot-container', {
          'essay-copilot-container--muted': !states.copilotStarted,
        }),
      [states.copilotStarted]
    );

    const summaryBlockClasses = useMemo(
      () =>
        classNames('summaryBlock', {
          'summaryBlock--preview': preview,
          'summaryBlock--focus': focus,
        }),
      [preview, focus]
    );

    const answerClasses = useMemo(
      () =>
        classNames('summaryBlock__answer', 'editor', {
          'editor--focus': focus,
          'summaryBlock__answer--essay': true,
        }),
      [focus]
    );

    const isChatEnded = useMemo(() => {
      return states.copilotEnded ? moment(states.copilotEnded).isValid() : false;
    }, [states.copilotEnded]);

    const handleViewAllPromptLibrary = useCallback(() => {
      setCurrentTab(TABS.PROMPT_LIBRARY);
    }, []);

    return (
      <>
        <div>
          <Button
            color="link"
            onClick={() => {
              setIsInstructionsOpen(true);
            }}
            className="mb-3"
          >
            Need help? View Instructions
          </Button>
          {!isChatEnded ? (
            <div className="essay-copilot-empty-state-container">
              <PiFolderSimplePlusLight size={40} />
              <h1>No Drafts Yet</h1>
              <p>
                Start with TedoGPT to create your response. Your finished draft will appear here for
                final edits and submission.
              </p>

              <Button
                color="default"
                onClick={handleOpenPane}
                className="d-flex align-items-center gap-1 rounded-pill btn-round mt-2"
              >
                <PiBrainBold size={20} style={{ color: '#fff' }} />
                <span>Start with TedoGPT</span>
              </Button>
            </div>
          ) : null}
        </div>
        <div className="summaryBlock__answer--copilotStartButtonContainer">
          {isChatEnded ? (
            <Button
              color="default"
              onClick={handleOpenPane}
              className="d-flex align-items-center gap-1 rounded-pill btn-round mt-2"
            >
              <PiBrainBold size={20} style={{ color: '#fff' }} />
              <span>TedoGPT</span>
            </Button>
          ) : null}
        </div>
        {isChatEnded ? (
          <div className={containerClasses}>
            <div className="headingBlock">
              <div className="w-100">
                <h1 className="title">Executive Summary</h1>
                <div className="d-flex justify-content-between w-100">
                  <span />
                  {/* <p className="subtitle">{options.subtitle ?? ''}</p> */}
                  <time className="year">{new Date().getFullYear()}</time>
                </div>
              </div>
            </div>
            <>
              <div className={summaryBlockClasses}>
                <div className={answerClasses}>
                  <Editor
                    ref={editorRef}
                    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                    readOnly={preview || disabled}
                    defaultValue={value}
                    onFocusIn={handleFocusIn}
                    onFocusOut={handleFocusOut}
                    onBlur={handleTextChangeBlur}
                  />
                </div>
              </div>
            </>
          </div>
        ) : null}
        <Instructions
          isOpen={isInstructionsOpen}
          setIsOpen={() => {
            setIsInstructionsOpen(true);
          }}
        />
        <SlidingPane
          isOpen={isOpen}
          hideHeader={true}
          onRequestClose={handleClosePane}
          className="essay-copilot-pane"
          width="100%"
        >
          <div className="essay-copilot-pane-white-overlay"></div>
          <Tabs.Root
            value={currentTab}
            defaultValue={currentTab}
            className="copilot-container-tabs"
            onValueChange={(value) => {
              setCurrentTab(value as TABS);
            }}
          >
            <Tabs.List className="copilot-container-tabs-list">
              <button
                onClick={handleClosePane}
                type="button"
                className="copilot-container-tabs-list-back-button"
              >
                <HiOutlineArrowLeft size={16} />
                <span>Back to Task</span>
              </button>

              <Tabs.Trigger value={TABS.CHAT} className="copilot-container-tabs-list-item">
                <PiChats />
                Chat
              </Tabs.Trigger>
              {!disabled && !preview ? (
                <>
                  <Tabs.Trigger
                    value={TABS.PROMPT_LIBRARY}
                    className="copilot-container-tabs-list-item"
                  >
                    <PiBookOpenText />
                    Prompt Library
                  </Tabs.Trigger>
                  <Tabs.Trigger
                    value={TABS.GUIDELINES}
                    className="copilot-container-tabs-list-item"
                  >
                    <PiClipboardText />
                    How-to Guide
                  </Tabs.Trigger>
                  <Tabs.Trigger
                    value={TABS.ASSESSMENT_GUIDELINES}
                    className="copilot-container-tabs-list-item"
                  >
                    <PiChecks />
                    Assessment Guidelines
                  </Tabs.Trigger>
                  <Tabs.Trigger value={TABS.RESOURCE} className="copilot-container-tabs-list-item">
                    <PiFileText />
                    Resource
                  </Tabs.Trigger>
                </>
              ) : null}
            </Tabs.List>
            <div className="copilot-container-tabs-content">
              <Tabs.Content value={TABS.CHAT}>
                <CopilotChatTabs
                  answer={answer}
                  currentAnswer={currentAnswer}
                  states={states}
                  messages={messages}
                  isOpen={isOpen}
                  disabled={disabled || Boolean(preview)}
                  pageElementId={pageElementId}
                  setIsOpen={setIsOpen}
                  viewAllPromptLibrary={handleViewAllPromptLibrary}
                  activityResource={options.activityResource}
                />
              </Tabs.Content>
              {!disabled && !preview ? (
                <>
                  <Tabs.Content value={TABS.GUIDELINES}>
                    <Guidelines />
                  </Tabs.Content>
                  <Tabs.Content value={TABS.PROMPT_LIBRARY}>
                    <PromptLibrary />
                  </Tabs.Content>
                  <Tabs.Content value={TABS.ASSESSMENT_GUIDELINES}>
                    <AssessmentGuidelines />
                  </Tabs.Content>
                  <Tabs.Content value={TABS.RESOURCE}>
                    <Resources activityResource={options.activityResource} />
                  </Tabs.Content>
                </>
              ) : null}
            </div>
          </Tabs.Root>
        </SlidingPane>
      </>
    );
  }
);

EssayCopilotView.displayName = 'EssayCopilotView';

export default EssayCopilotView;

// Editor is an uncontrolled React component
interface EditorProps {
  readOnly?: boolean;
  defaultValue?: string;
  onTextChange?: (delta: unknown, oldContents: unknown, source: string) => void;
  onSelectionChange?: (
    range: { index: number; length: number } | null,
    oldRange: { index: number; length: number } | null,
    source: string
  ) => void;
  onFocusIn?: () => void;
  onFocusOut?: () => void;
  onBlur?: () => void;
}

const Editor = memo(
  forwardRef<Quill, EditorProps>(
    (
      { readOnly, defaultValue, onTextChange, onSelectionChange, onFocusIn, onFocusOut, onBlur },
      ref
    ) => {
      const containerRef = useRef<HTMLDivElement>(null);
      const defaultValueRef = useRef(defaultValue);
      const onTextChangeRef = useRef(onTextChange);
      const onSelectionChangeRef = useRef(onSelectionChange);
      const onFocusInRef = useRef(onFocusIn);
      const onFocusOutRef = useRef(onFocusOut);
      const onBlurRef = useRef(onBlur);
      const quillInstanceRef = useRef<Quill | null>(null);
      const isToolbarClickedRef = useRef(false);
      const [isInitialized, setIsInitialized] = useState(false);

      useLayoutEffect(() => {
        onTextChangeRef.current = onTextChange;
        onSelectionChangeRef.current = onSelectionChange;
        onFocusInRef.current = onFocusIn;
        onFocusOutRef.current = onFocusOut;
        onBlurRef.current = onBlur;
      });

      useEffect(() => {
        if (ref && 'current' in ref && ref.current) {
          ref.current.enable(!readOnly);
        }
      }, [ref, readOnly]);

      // Handle defaultValue changes
      useEffect(() => {
        if (isInitialized && quillInstanceRef.current && defaultValue !== defaultValueRef.current) {
          defaultValueRef.current = defaultValue;
          const currentContent = quillInstanceRef.current.getContents();

          // Convert HTML to Quill content
          const tempDiv = document.createElement('div');
          // Sanitize HTML before setting innerHTML
          const sanitizedHtml = DOMPurify.sanitize(defaultValue ?? '');
          tempDiv.innerHTML = sanitizedHtml;
          const newContent = quillInstanceRef.current.clipboard.convert({
            html: tempDiv.innerHTML,
          });

          // Only update if content is actually different
          if (JSON.stringify(currentContent) !== JSON.stringify(newContent)) {
            quillInstanceRef.current.setContents(newContent, 'api');
          }
        }
      }, [defaultValue, isInitialized]);

      // Function to toggle toolbar visibility
      const toggleToolbarVisibility = (show: boolean) => {
        if (containerRef.current) {
          const toolbarElement = containerRef.current.querySelector('.ql-toolbar');
          if (toolbarElement) {
            (toolbarElement as HTMLElement).style.display = show ? 'block' : 'none';
          }
        }
      };

      useEffect(() => {
        // Import Quill dynamically to avoid SSR issues
        import('quill')
          .then((QuillModule) => {
            const Quill = QuillModule.default;

            if (!containerRef.current) return;

            const container = containerRef.current;
            // Clear any existing content first
            container.innerHTML = '';
            const editorContainer = container.appendChild(
              container.ownerDocument.createElement('div')
            );

            // Add custom class to the container for specific styling
            container.classList.add('executive-summary-editor');

            // Configure Quill with similar toolbar options to TinyMCE, but without color options
            const quill = new Quill(editorContainer, {
              theme: 'snow',
              modules: {
                toolbar: [
                  ['bold', 'italic', 'underline'],
                  [{ size: [] }],
                  [{ align: [] }],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                  ['clean'],
                ],
              },
              placeholder: 'Type your answer here...',
              readOnly: readOnly,
              formats: ['bold', 'italic', 'underline', 'size', 'align', 'list'], // Explicitly specify allowed formats
            });

            // Set default font
            quill.root.style.fontFamily =
              "'Helvetica Now Display', 'Helvetica Neue', Helvetica, Arial, sans-serif";
            quill.root.style.fontWeight = '300';

            quillInstanceRef.current = quill;

            if (ref && 'current' in ref) {
              ref.current = quill;
            }

            if (defaultValueRef.current) {
              // Sanitize HTML before converting to Delta
              const sanitizedHtml = DOMPurify.sanitize(defaultValueRef.current);
              const delta = quill.clipboard.convert({
                html: sanitizedHtml,
              });
              quill.setContents(delta, 'api');
            }

            // Mark as initialized after setting up the editor
            setIsInitialized(true);

            quill.on('text-change', (delta, oldContents, source) => {
              onTextChangeRef.current?.(delta, oldContents, source);
            });

            quill.on('selection-change', (range, oldRange, source) => {
              onSelectionChangeRef.current?.(range, oldRange, source);
            });

            // Hide toolbar initially
            toggleToolbarVisibility(false);

            // Add event listeners to the toolbar to prevent it from hiding when clicked
            const toolbarElement = container.querySelector('.ql-toolbar');
            if (toolbarElement) {
              toolbarElement.addEventListener('mousedown', (e) => {
                isToolbarClickedRef.current = true;
                e.preventDefault(); // Prevent default to avoid losing focus
              });

              toolbarElement.addEventListener('mouseup', () => {
                // Reset after a short delay to allow the editor to regain focus
                setTimeout(() => {
                  isToolbarClickedRef.current = false;
                }, 100);
              });
            }

            // Handle focus events
            editorContainer.addEventListener(
              'focus',
              () => {
                toggleToolbarVisibility(true);
                onFocusInRef.current?.();
              },
              true
            );

            editorContainer.addEventListener(
              'blur',
              (e) => {
                // Only hide toolbar if we're not clicking on the toolbar itself
                if (!isToolbarClickedRef.current) {
                  // Check if the related target is within the toolbar
                  const toolbarElement = container.querySelector('.ql-toolbar');
                  if (toolbarElement && !toolbarElement.contains(e.relatedTarget as Node)) {
                    toggleToolbarVisibility(false);
                    onFocusOutRef.current?.();
                    onBlurRef.current?.();
                  }
                } else {
                  // Call onBlur even if toolbar was clicked, as the editor still lost focus
                  onBlurRef.current?.();
                }
              },
              true
            );

            return () => {
              if (ref && 'current' in ref) {
                ref.current = null;
              }

              // Remove event listeners if container still exists
              if (containerRef.current) {
                const toolbarElement = containerRef.current.querySelector('.ql-toolbar');
                if (toolbarElement) {
                  // Clean up all event listeners
                  toolbarElement.replaceWith(toolbarElement.cloneNode(true));
                }

                const editorContainer = containerRef.current.querySelector('.ql-container');
                if (editorContainer) {
                  editorContainer.replaceWith(editorContainer.cloneNode(true));
                }
              }

              container.innerHTML = '';
            };
          })
          .catch((error: unknown) => {
            logger.error(error);
          });
      }, [ref, readOnly]);

      return <div ref={containerRef} className="executive-summary-editor"></div>;
    }
  )
);

Editor.displayName = 'Editor';

interface MessageProps {
  message: ChatItem;
  assistantAvatar: string;
  waitingForFirstChunk?: boolean;
}

export const Message = memo<MessageProps>(
  ({ message, assistantAvatar, waitingForFirstChunk = false }) => {
    const user = useAppSelector((state) => state.users);

    // Memoize user avatar to prevent unnecessary re-renders
    const userAvatar = useMemo(
      () => user.user.attributes.avatarUrl,
      [user.user.attributes.avatarUrl]
    );

    if (message.sender === 'assistant') {
      return (
        <div
          key={message.id}
          className="chatbox__bubble__container chatbox__bubble__container__assistant"
        >
          <div className="chatbox__bubble__container__assistant__inner">
            <div>
              {waitingForFirstChunk ? (
                <span className="chatbox__bubble__container__assistant__loading">{Loading}</span>
              ) : (
                <span className="chatbox__bubble__container__assistant__text">
                  <Markdown remarkPlugins={[remarkGfm, remarkBreaks]}>{message.message}</Markdown>
                </span>
              )}
            </div>
          </div>
          <img
            src={assistantAvatar}
            alt="My profile"
            className="chatbox__bubble__container__assistant__img"
            width={144}
            height={144}
          />
        </div>
      );
    }

    // message.sender can only be 'user' or 'assistant'
    return (
      <div key={message.id} className="chatbox__bubble__container chatbox__bubble__container__user">
        <div className="chatbox__bubble__container__user__inner">
          <div>
            <span className="chatbox__bubble__container__user__text">
              <span className="chatbox__bubble__container__user__markdown">
                <Markdown remarkPlugins={[remarkGfm, remarkBreaks]}>{message.message}</Markdown>
              </span>
              <div className="chatbox__bubble__container__user__timestamp">
                {moment(message.timestamp).format('h:mm A')}
              </div>
            </span>
          </div>
        </div>
        <img
          src={userAvatar}
          alt="My profile"
          className="chatbox__bubble__container__user__img"
          width={144}
          height={144}
        />
      </div>
    );
  }
);

interface CopilotChatTabsProps {
  answer?: (answer: EssayCopilotAnswer) => void;
  currentAnswer: EssayCopilotAnswer | undefined;
  states: { copilotStarted: string | null; copilotEnded: string | null };
  messages: ChatItem[];
  isOpen: boolean;
  disabled: boolean;
  pageElementId: string;
  setIsOpen: (isOpen: boolean) => void;
  viewAllPromptLibrary: () => void;
  activityResource: string;
}

const CopilotChatTabs = memo<CopilotChatTabsProps>(
  ({
    answer,
    currentAnswer,
    states,
    messages,
    isOpen,
    disabled,
    pageElementId,
    setIsOpen,
    viewAllPromptLibrary,
    activityResource,
  }) => {
    const projectId = useAppSelector((state) => state.projects.currentProject.project.id);

    const [inputValue, setInputValue] = useState('');
    const [doneConfirmModal, setDoneConfirmModal] = useState(false);
    const [doneLoading, setDoneLoading] = useState(false);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const [textareaHeight, setTextareaHeight] = useState(0);

    // Memoize the onMessageUpdate callback to prevent unnecessary re-renders
    const onMessageUpdate = useCallback(
      (updatedMessages: ChatMessage[]) => {
        const started = states.copilotStarted ?? new Date().toISOString();
        answer?.({
          ...currentAnswer,
          essayCopilotMessages: updatedMessages,
          states: {
            ...states,
            copilotStarted: started,
          },
        } as EssayCopilotAnswer);
      },
      [answer, currentAnswer, states]
    );

    const onError = useCallback((error: Error) => {
      logger.error(`Streaming chat error: ${error.message}`);
    }, []);

    // Use the streaming chat hook
    const { isStreaming, isLoading, waitingForFirstChunk, sendMessage } = useStreamingChat({
      onMessageUpdate,
      onError,
      tags: ['essay-copilot', 'cartedo'],
      systemPrompt: SYSTEM_PROMPT(activityResource),
    });

    // Function to scroll chat container to bottom with debouncing
    const scrollToBottom = useCallback(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    }, []);

    const handleSubmit = useCallback(
      (e: React.FormEvent<HTMLFormElement> | React.KeyboardEvent<HTMLTextAreaElement>) => {
        e.preventDefault();
        if (inputValue.trim() === '' || disabled) return;

        // Update states to mark copilot as started
        answer?.({
          ...currentAnswer,
          states: {
            ...states,
            copilotStarted: new Date().toISOString(),
          },
        } as EssayCopilotAnswer);

        // Send message using the hook
        void sendMessage(inputValue.trim(), messages);
        setInputValue('');
      },
      [inputValue, disabled, currentAnswer, states, answer, sendMessage, messages]
    );

    // Compute done button visibility
    const showDoneButton = useMemo(() => {
      if (messages.length === 0) return false;
      const hasUserMessages = messages.some((m) => m.sender === 'user');
      const assistantCount = messages.filter((m) => m.sender === 'assistant').length;
      return hasUserMessages && assistantCount > 0;
    }, [messages]);

    // Effect to scroll to bottom when messages change
    useEffect(() => {
      scrollToBottom();
    }, [messages, scrollToBottom]);

    // Effect to scroll to bottom when pane opens
    useEffect(() => {
      if (isOpen) {
        // Small delay to ensure the pane is fully rendered
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
    }, [isOpen, scrollToBottom]);

    // Memoize the GPT messages transformation for the done button
    const gptMessages = useMemo(
      () =>
        messages.map((message) => ({
          role: message.sender,
          content: message.message,
        })),
      [messages]
    );

    const handleDone = useCallback(() => {
      setDoneLoading(true);
      Api.getEssayCopilotEndChat(projectId, pageElementId, gptMessages)
        .then((response) => {
          const essay = marked(response.data.essay);
          answer?.({
            ...currentAnswer,
            states: {
              ...states,
              copilotEnded: new Date().toISOString(),
            },
            essay,
          } as EssayCopilotAnswer);
          setIsOpen(false);
        })
        .catch(() => {
          logger.error('Error getting completions');
        })
        .finally(() => {
          setDoneLoading(false);
          setDoneConfirmModal(false);
        });
    }, [projectId, pageElementId, gptMessages, answer, currentAnswer, states, setIsOpen]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInputValue(e.target.value);
    }, []);
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          handleSubmit(e);
        }
      },
      [handleSubmit]
    );

    const handleDoneConfirm = useCallback(() => {
      setDoneConfirmModal(true);
    }, []);
    const handleDoneCancel = useCallback(() => {
      setDoneConfirmModal(false);
    }, []);

    const searchBoxClasses = useMemo(
      () =>
        classNames(
          'chatbot-search-box-container',
          disabled ? 'chatbot-search-box-container-disabled' : null
        ),
      [disabled]
    );

    const isChatStarted = useMemo(() => {
      return states.copilotStarted ? moment(states.copilotStarted).isValid() : false;
    }, [states.copilotStarted]);

    // Memoize inline styles to prevent recreation on every render
    const headerPlaceholderStyle = useMemo(() => ({ height: '43px', width: '43px' }), []);
    const chatContainerHeightStyle = useMemo(
      () => ({
        height: !isChatStarted
          ? `calc(-165px + 100vh - ${textareaHeight.toString()}px)`
          : `calc(-93px + 100vh - ${textareaHeight.toString()}px)`,
      }),
      [isChatStarted, textareaHeight]
    );
    const inputWrapperStyle = useMemo(
      () => ({
        display: 'flex',
        gap: '0.2rem',
        alignItems: 'center',
        flex: '1',
      }),
      []
    );

    // Memoize shuffled prompt library items
    const shuffledPromptLibraryItems = useMemo(() => {
      return shuffleArray(PROMPT_LIBRARY_ITEMS);
    }, []);

    const [isInstructionsOpen, setIsInstructionsOpen] = useState(false);

    return (
      <>
        <div className="essay-copilot-pane__container">
          <div className="essay-copilot-pane__header">
            <div>
              <div className="essay-copilot-pane__header__logo">
                <CopilotImage size={40} />
                TedoGPT
              </div>
            </div>
            <div className="d-flex align-items-center gap-4">
              <Button
                color="link"
                onClick={() => {
                  setIsInstructionsOpen(true);
                }}
                className="d-flex align-items-center gap-1"
              >
                <PiBookOpenText size={20} />
                <span>View Instructions</span>
              </Button>
              {!disabled ? (
                <Button
                  color="default"
                  type="button"
                  className="essay-copilot-pane__header__doneButton rounded-pill btn-round"
                  onClick={handleDoneConfirm}
                  disabled={!showDoneButton}
                >
                  <MdDone size={20} />
                  Apply Draft
                </Button>
              ) : (
                <div style={headerPlaceholderStyle}></div>
              )}
            </div>
          </div>
          <div className="essay-copilot-pane__content">
            <div className="chatbox-container">
              <div className="chatbox-body">
                <div
                  className="chatbox__body__container"
                  ref={chatContainerRef}
                  style={chatContainerHeightStyle}
                >
                  {messages.length === 0 ? (
                    <div className="copilot-chat-container-empty-state">
                      <CopilotImage size={100} />
                      <h1>Hello! I&apos;m TedoGPT 👋</h1>
                      <p>
                        I can help you brainstorm, draft, and refine your task responses using AI
                        just like ChatGPT or Gemini.
                      </p>
                      <p className="copilot-chat-container-empty-state__tip">
                        <PiLightbulb size={20} />
                        <span className="copilot-chat-container-empty-state__tip-text">
                          Tip: TedoGPT is your co-creator. To get started, simply{' '}
                          <Button
                            color="link"
                            onClick={() => {
                              setIsInstructionsOpen(true);
                            }}
                          >
                            view the instructions
                          </Button>{' '}
                          and begin creating.
                        </span>
                      </p>
                    </div>
                  ) : (
                    <div className="chatbox__body__container__inner">
                      {messages.map((message) => {
                        return (
                          <Message
                            message={message}
                            key={message.id}
                            assistantAvatar={assistantAvatar}
                            waitingForFirstChunk={
                              waitingForFirstChunk &&
                              message.sender === 'assistant' &&
                              message.message === ''
                            }
                          />
                        );
                      })}
                      {isLoading && !isStreaming ? (
                        <div className="d-flex align-items-center mt-4">
                          <div className="chatbox__bubble__container chatbox__bubble__container__assistant">
                            <div className="chatbox__bubble__container__assistant__inner">
                              <div>
                                <span className="chatbox__bubble__container__assistant__loading">
                                  {Loading}
                                </span>
                              </div>
                            </div>
                            <img
                              src={assistantAvatar}
                              alt="My profile"
                              className="chatbox__bubble__container__assistant__img"
                              width={144}
                              height={144}
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
                <div className="chatbox-footer">
                  {!isChatStarted ? (
                    <div className="chatbox-footer__prompt-library">
                      <div className="chatbox-footer__prompt-library-items">
                        {shuffledPromptLibraryItems.slice(0, 4).map((item) => (
                          <button
                            type="button"
                            className="chatbox-footer__prompt-library-item"
                            key={item.title}
                            onClick={() => {
                              setInputValue(item.sample_prompt);
                            }}
                          >
                            <PiMagicWand size={20} />
                            <span>{item.title}</span>
                          </button>
                        ))}
                        <Button
                          color="link"
                          className="chatbox-footer__prompt-library-item-more d-flex align-items-center gap-1"
                          onClick={(e) => {
                            e.preventDefault();
                            viewAllPromptLibrary();
                          }}
                          type="button"
                        >
                          <span>View All</span>
                          <PiArrowRight size={20} />
                        </Button>
                      </div>
                    </div>
                  ) : null}

                  <form className={searchBoxClasses} onSubmit={handleSubmit}>
                    <div style={inputWrapperStyle}>
                      <button
                        type="button"
                        className="chatbot-view-all-prompt-library-button"
                        id="chatbot-view-all-prompt-library-button"
                        onClick={(e) => {
                          e.preventDefault();
                          viewAllPromptLibrary();
                        }}
                      >
                        <PiBookOpenText
                          className="chatbot-view-all-prompt-library-button-icon"
                          size={24}
                        />
                      </button>
                      <UncontrolledTooltip target="chatbot-view-all-prompt-library-button">
                        View All Prompt Library
                      </UncontrolledTooltip>
                      <div className="chatbot-input-wrapper">
                        <ReactTextareaAutosize
                          id="essay-copilot-pane__input"
                          value={inputValue}
                          onChange={handleInputChange}
                          onKeyDown={handleKeyDown}
                          rows={1}
                          maxRows={10}
                          onHeightChange={(height) => {
                            const newHeight = Math.min(height + 64, 400);
                            setTextareaHeight(newHeight);
                          }}
                          placeholder={
                            disabled
                              ? translate('chat.chatEndedOrLocked')
                              : translate('chat.askMyAnything')
                          }
                          disabled={disabled}
                        ></ReactTextareaAutosize>
                      </div>
                    </div>
                    <div>
                      <button
                        className="essay-copilot-pane__sendMessageButton"
                        type="submit"
                        disabled={disabled}
                      >
                        <IoIosSend size={32} style={{ color: '#0ea5e9' }} />
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Alert isOpen={doneConfirmModal} onRequestClose={handleDoneCancel} showCloseButton>
          <Row className="py-3">
            <Col>
              <ImagePlaceholder src={smile} height="60" width="60" native />
            </Col>
          </Row>
          <Row className="pt-3">
            <Col>
              <H1 className="font-weight-500 text-dark">Ready to Apply Your Latest Draft?</H1>
            </Col>
          </Row>
          <Row className="pt-2 pb-3">
            <Col>
              <p className="px-3 font-weight-400 text-dark">
                You're about to apply your latest draft to the submission template, replacing any
                previous versions. Return to TedoGPT anytime to refine it before final submission.
                <br />
                <br />
                <strong>
                  Note: This isn't your final submission — you'll finalize and submit in Step 2.
                </strong>
              </p>
            </Col>
          </Row>
          <Row>
            {doneLoading ? (
              <Col>
                <Indicator />
              </Col>
            ) : (
              <>
                <Col className="px-1 mx-0 mb-2 text-center text-sm-right">
                  <Button color="grey" onClick={handleDoneCancel}>
                    {translate('essayCopilot.doneConfirmModal.cancelButton')}
                  </Button>
                </Col>
                <Col className="px-1 mx-0 mb-2 text-center text-sm-left">
                  <Button color="info" onClick={handleDone}>
                    Apply Draft
                  </Button>
                </Col>
              </>
            )}
          </Row>
        </Alert>
        <Instructions
          isOpen={isInstructionsOpen}
          setIsOpen={() => {
            setIsInstructionsOpen(false);
          }}
        />
      </>
    );
  }
);

Message.displayName = 'Message';

interface CopilotImageProps {
  size: number;
}

const CopilotImage = memo<CopilotImageProps>(({ size }) => (
  <div>
    <img src={assistantAvatar} height={size} width={size} alt="Copilot Assistant" />
  </div>
));

const Resources = memo(({ activityResource }: { activityResource: string }) => {
  return (
    <div className="essay-copilot-pane__resources">
      <h1 className="essay-copilot-pane__guidelines__heading">Resource</h1>
      <div className="essay-copilot-pane__resources__viewer">
        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(activityResource) }} />
      </div>
    </div>
  );
});

Resources.displayName = 'Resources';

// Moved outside component to prevent recreation on every render
const PROMPT_LIBRARY_ITEMS = [
  {
    title: 'Define the task goal clearly',
    description:
      'Clarify the main objective of the task in simple, action-focused language to ensure the deliverable’s purpose is understood.',
    sample_prompt:
      'Help me restate the task goal in clear, action-oriented terms that describe exactly what the deliverable should achieve.',
  },
  {
    title: 'Describe the company or scenario',
    description:
      'Set the context for the task by explaining the company, industry, or situation in which the task takes place.',
    sample_prompt:
      'The task is set in [company/industry]. Here’s the background: [insert info]. Use this context to make the response realistic.',
  },
  {
    title: 'Outline task requirements',
    description:
      'Summarize the key rules, structure, and formatting requirements for the response to ensure alignment with expectations.',
    sample_prompt:
      'The response must follow these rules: [insert format, tone, or length]. Keep all outputs aligned with these.',
  },
  {
    title: 'Clarify your role',
    description:
      'Adopt the correct persona or professional role to ensure the response reflects appropriate responsibilities and authority.',
    sample_prompt:
      'Assume I am a [insert role]. Write from my perspective and reflect my decision-making scope.',
  },
  {
    title: 'State success criteria',
    description:
      'Define clear outcomes that indicate success. Reference these criteria explicitly in the response to maintain focus.',
    sample_prompt:
      'A strong answer should include: [insert outcomes]. Use these as benchmarks for success while writing.',
  },
  {
    title: 'Add tone and audience',
    description:
      'Specify the tone and intended audience so the response fits the communication context and expectations.',
    sample_prompt:
      'Write in a [insert tone: professional/formal/persuasive/informative] voice for an audience of [describe audience].',
  },
  {
    title: 'Ask for structure first',
    description:
      'Before writing the full draft, propose an organized outline with sections and subpoints to confirm structure and flow.',
    sample_prompt:
      'Propose a detailed outline with section headings and key bullet points before drafting the full version.',
  },
  {
    title: 'Summarize key constraints',
    description:
      'List the main dos and don’ts to keep every section compliant with requirements, noting any trade-offs made.',
    sample_prompt:
      'Here are the constraints: [insert dos/don’ts]. Keep all sections compliant and highlight any trade-offs.',
  },
  {
    title: 'Set a target length',
    description:
      'Define an approximate target word count to calibrate depth, examples, and level of detail in the draft.',
    sample_prompt:
      'Target length: ~[X words]. Adjust content depth and section balance to fit within this limit.',
  },
  {
    title: 'Refine tone and clarity',
    description:
      'Edit or rewrite text to improve professionalism, flow, and clarity while preserving the original meaning.',
    sample_prompt:
      'Rewrite this text to sound more professional, natural, and polished without changing its meaning: [paste text].',
  },
  {
    title: 'Add examples or data',
    description:
      'Support arguments or explanations with realistic examples or compact, relevant data to increase credibility.',
    sample_prompt:
      'Enhance [insert section] with examples or brief data points that make it more concrete and convincing.',
  },
  {
    title: 'Rephrase AI text in your words',
    description:
      'Paraphrase AI-generated text in your own voice and style while maintaining the same core ideas.',
    sample_prompt:
      'Paraphrase this text in my voice (style: [describe your style]) while keeping the original meaning: [paste text].',
  },
  {
    title: 'Simplify complex points',
    description:
      'Reword dense or technical explanations using plain language and simple analogies to make them accessible.',
    sample_prompt:
      'Simplify this explanation for a smart non-expert while keeping it accurate. Use plain language or a short analogy: [paste text].',
  },
  {
    title: 'Ensure logical flow',
    description:
      'Review a draft for coherence and smooth transitions, suggesting specific rewrites to improve structure.',
    sample_prompt:
      'Review this draft for logical flow and propose clearer transitions or sequence improvements: [paste text].',
  },
  {
    title: 'Turn bullet points into paragraphs',
    description:
      'Convert bullet lists into cohesive paragraphs with clear topic sentences and smooth linking transitions.',
    sample_prompt:
      'Turn these bullets into connected paragraphs with good flow and clear topic sentences: [paste bullets].',
  },
  {
    title: 'Highlight missing information',
    description:
      'Audit a draft for unclear or missing details and list precise questions to resolve these gaps.',
    sample_prompt:
      'Review this draft and list what’s missing or unclear. Suggest targeted questions to fill those gaps: [paste draft].',
  },
  {
    title: 'Add human insight',
    description:
      'Integrate reasoning, insights, or judgment into an existing draft where it strengthens the argument.',
    sample_prompt:
      'Blend these insights into the draft where they add value. Show integration points briefly: [insert reasoning] + [paste draft].',
  },
  {
    title: 'Run a self-check',
    description:
      'Evaluate a draft for completeness, clarity, and quality. Provide a QA checklist and suggest final tweaks.',
    sample_prompt:
      'Evaluate this draft for clarity, completeness, and quality. Give a short QA checklist and improvement notes: [paste draft].',
  },
  {
    title: 'Summarize final points',
    description:
      'Condense the main message into a short summary emphasizing key recommendations and rationale.',
    sample_prompt:
      'Summarize the piece in 2–3 concise sentences that capture the main recommendation and reasoning: [paste draft].',
  },
  {
    title: 'Prepare for submission',
    description:
      'Perform a final quality check covering tone, accuracy, and formatting, then provide a ready-to-submit version.',
    sample_prompt:
      'Do a last review for tone, accuracy, and completeness. Output (1) a checklist with pass/fail notes and (2) the final submission-ready version: [paste draft] + [style/format rules].',
  },
] as const satisfies {
  title: string;
  description: string;
  sample_prompt: string;
}[];

const PromptLibrary = memo(() => {
  // Memoize items to prevent recreation on every render
  const items = useMemo(
    () =>
      PROMPT_LIBRARY_ITEMS.map((item) => ({
        ...item,
        id: nanoid(),
      })),
    []
  );

  return (
    <div className="essay-copilot-pane__prompt-library">
      <h1 className="essay-copilot-pane__prompt-library__title">Prompt Library</h1>
      <div className="essay-copilot-pane__prompt-library__items">
        {items.map((item) => (
          <PromptLibraryItem key={item.id} item={item} />
        ))}
      </div>
    </div>
  );
});

const PromptLibraryItem = memo(
  ({
    item,
  }: {
    item: {
      id: string;
      title: string;
      description: string;
      sample_prompt: string;
    };
  }) => {
    const [modalOpen, setModalOpen] = useState(false);

    // Memoize handlers to prevent unnecessary re-renders
    const handleModalOpen = useCallback(() => {
      setModalOpen(true);
    }, []);
    const handleModalClose = useCallback(() => {
      setModalOpen(false);
    }, []);

    const handleCopyPrompt = useCallback(() => {
      navigator.clipboard
        .writeText(item.sample_prompt)
        .then(() => {
          successToast('Prompt copied to clipboard');
        })
        .catch(() => {
          errorToast('Failed to copy prompt to clipboard');
        });
    }, [item.sample_prompt]);
    return (
      <div>
        <button
          type="button"
          className="essay-copilot-pane__prompt-library__item"
          onClick={handleModalOpen}
        >
          <div className="essay-copilot-pane__prompt-library__item__header">
            <div className="essay-copilot-pane__prompt-library__item__icon">
              <PiKeyboard />
            </div>
            <h3 className="essay-copilot-pane__prompt-library__item__title">{item.title}</h3>
          </div>
          <p className="essay-copilot-pane__prompt-library__item__description">
            {item.description}
          </p>
        </button>
        <Modal isOpen={modalOpen} toggle={handleModalClose} size="md">
          <h1 className="essay-copilot-pane__prompt-library__item__title">{item.title}</h1>
          <p className="essay-copilot-pane__prompt-library__item__description">
            {item.description}
          </p>
          <p className="essay-copilot-pane__prompt-library__item__sample-prompt">
            {item.sample_prompt}
            <button
              type="button"
              className="essay-copilot-pane__prompt-library__item__sample-prompt__copy"
              id={`essay-copilot-pane__prompt-library__item__sample-prompt__copy-${item.id}`}
              onClick={handleCopyPrompt}
            >
              <FiCopy />
            </button>
            <UncontrolledTooltip
              target={`essay-copilot-pane__prompt-library__item__sample-prompt__copy-${item.id}`}
              delay={0}
              placement="top"
            >
              Copy to clipboard
            </UncontrolledTooltip>
          </p>
        </Modal>
      </div>
    );
  }
);

PromptLibrary.displayName = 'PromptLibrary';

const Guidelines = memo(() => (
  <div className="essay-copilot-pane__guidelines">
    <h1 className="essay-copilot-pane__guidelines__heading">How-to Guide</h1>
    <div>
      <p className="essay-copilot-pane__guidelines__text">
        In this task, you’ll complete two subtasks: first, use TedoGPT an AI similar to ChatGPT to
        co-create your answer through guided interaction; then bring your final draft into the task
        template to make any final edits before submitting. Your performance is assessed on both how
        you use TedoGPT and what you produce, reflecting your ability to collaborate with AI while
        demonstrating your own reasoning and domain knowledge.
      </p>
      <p className="essay-copilot-pane__guidelines__text">
        Use this LLM style chat to collaborate with TedoGPT and create realistic, well-structured
        task submissions like you would on ChatGPT, Gemini, Claude etc.
      </p>
      <h1 className="essay-copilot-pane__guidelines__sub-heading">
        🧠 &nbsp;What TedoGPT Already Knows
      </h1>
      <p>
        TedoGPT understands how to generate text, ideas, and examples but not your specific
        scenario. You’ll need to explain your company, situation, and task clearly (e.g., “You are a
        marketing intern writing a report for a retail brand”).
      </p>
      <h1 className="essay-copilot-pane__guidelines__sub-heading">
        💡 &nbsp;How to Modify or Create Responses
      </h1>
      <p>
        <ul>
          <li>
            <strong>Chat naturally:</strong> Tell TedoGPT your context, role, and goals in plain
            language
          </li>
          <li>
            <strong>Use the Prompt Library:</strong> Choose ready-made prompts to refine or expand
            your draft
          </li>
          <li>
            <strong>Preview & Edit:</strong> Review what TedoGPT generates add your own reasoning,
            examples, and checks
          </li>
        </ul>
      </p>
      <h1 className="essay-copilot-pane__guidelines__sub-heading">✅ &nbsp;Apply Changes</h1>
      <p>Click Apply Changes to update your task draft instantly.</p>
    </div>
  </div>
));

const AssessmentGuidelines = memo(() => (
  <div className="essay-copilot-pane__guidelines">
    <h1 className="essay-copilot-pane__guidelines__heading">Assessment Guidelines</h1>
    <div>
      <h1 className="essay-copilot-pane__guidelines__sub-heading">
        💡 &nbsp;What This Activity Measures
      </h1>
      <p>
        You’ll be evaluated on your engagement with TedoGPT and the quality of your final
        submission. The system observes how you prompt, refine, and apply judgment rewarding
        independent thinking, creativity, and the correct use of domain expertise.
      </p>
      <h1 className="essay-copilot-pane__guidelines__sub-heading">📊 &nbsp;Assessment Focus</h1>
      <p>Your work is graded across seven key dimensions:</p>
      <ul className="essay-copilot-pane__guidelines__list">
        <li>
          <strong>Problem Framing:</strong> How clearly you define the task and context.
        </li>
        <li>
          <strong>Critical Thinking:</strong> How effectively you question and refine AI outputs.
        </li>
        <li>
          <strong>Creativity:</strong> How you explore alternatives and generate fresh ideas.
        </li>
        <li>
          <strong>Prompting & Efficiency:</strong> How strategically you guide the AI.
        </li>
        <li>
          <strong>Domain Application:</strong> How well you apply your subject knowledge.
        </li>
        <li>
          <strong>Verification:</strong> How carefully you check accuracy and bias.
        </li>
        <li>
          <strong>Ownership:</strong> How cohesive and human your final output feels.
        </li>
      </ul>
      <h1 className="essay-copilot-pane__guidelines__sub-heading">⚠️ &nbsp;Important!</h1>
      <p>
        Do not use any external LLMs outside Cartedo. Your grade depends on your interaction with
        TedoGPT, and submissions without evidence of AI collaboration may receive a zero. Think of
        this like a math problem credit is given for both your process (AI interaction) and your
        final answer. Skipping either step will reduce your score.
      </p>
      <h1 className="essay-copilot-pane__guidelines__sub-heading">🌱 &nbsp;General Guidelines</h1>
      <p>
        Treat TedoGPT as a thinking partner. Give it context, challenge its ideas, and refine
        outputs with your own insights. The best submissions reflect balance AI-assisted yet
        grounded in your own reasoning, creativity, and professional judgment.
      </p>
    </div>
  </div>
));

Guidelines.displayName = 'Guidelines';

function shuffleArray<T>(array: readonly T[]): T[] {
  return [...array].sort(() => Math.random() - 0.5);
}

function Instructions({ isOpen, setIsOpen }: { isOpen: boolean; setIsOpen: () => void }) {
  return (
    <Modal isOpen={isOpen} toggle={setIsOpen} width="740px">
      <h1 className="essay-copilot-pane__guidelines__heading">
        Step-by-Step Instructions
        <p>
          This is a two-part activity. Before starting, take a moment to review all the steps below.
        </p>
      </h1>
      <p className="essay-copilot-pane__instructions__text">
        <strong>STEP 1: Explore TedoGPT</strong>
        <ul>
          <li>
            Click on <strong>TedoGPT</strong> and look through all tabs
            <ul>
              <li>
                <strong>Chat</strong> – works like ChatGPT.{' '}
              </li>
              <li>
                <strong>Prompt Library</strong> – provides starter prompts to help you begin.{' '}
              </li>
              <li>
                <strong>Resource</strong> – contains the main resource/dataset for this activity.{' '}
              </li>
              <li>
                <strong>Guidelines</strong> – shows how your AI-assisted work will be assessed.
              </li>
              <br />
            </ul>
          </li>
          <li>
            <strong>Use TedoGPT with the provided resource</strong>
            <ul>
              <li>Start chatting with TedoGPT as you would with ChatGPT. </li>
              <li>
                TedoGPT already has the resources/ dataset for this activity, so you don’t need to
                provide it again, simply ask to use the resource data in your chat{' '}
              </li>
              <li>
                If TedoGPT doesn’t recall the resource, copy and paste it from the Resource tab
                inside TedoGPT
              </li>
            </ul>
          </li>
        </ul>
        <hr />
        <strong>STEP 2: Co-create your submission</strong>
        <ul>
          <li>
            Work with TedoGPT to draft and refine your final deliverable, whatever the activity
            requires.{' '}
          </li>
          <li>
            Check for bias, ensure accuracy, and apply your domain knowledge to strengthen the work.
          </li>
        </ul>
        <hr />
        <strong>STEP 3: Transfer your draft to the activity template</strong>
        <ul>
          <li>
            When your draft in TedoGPT is ready, click <strong>Apply draft</strong> to move it into
            the appropriate activity template for final review.{' '}
          </li>
          <li>
            <strong>Add the AI disclaimer:</strong> Include a footer note clearly stating that the
            submission was <strong>co-created using AI</strong>
          </li>
        </ul>
        <hr />
        <strong>STEP 4: Final review, formatting and submit your work</strong>
        <ul>
          <li>
            Format, revise, and refine your draft for clarity, accuracy, and professionalism.{' '}
          </li>
          <li>
            When your final deliverable is complete, click <strong>Next</strong> to submit it for
            your manager’s review.
          </li>
        </ul>
      </p>
    </Modal>
  );
}

Instructions.displayName = 'Instructions';
